<template>
  <Modal :value="value" width="xl" @input="$emit('input', $event)">
    <span slot="header">Revisão do cadastro em lote</span>

    <div class="filter-cards">
      <button
        type="button"
        class="filter-card filter-card--all"
        :class="{ 'is-active': activeFilter === 'all' }"
        @click="activeFilter = 'all'"
      >
        <span class="filter-card__dot"></span>
        <span class="filter-card__label">Todos</span>
        <span class="filter-card__count">{{ localRows.length }}</span>
      </button>

      <button
        type="button"
        class="filter-card filter-card--errors"
        :class="{ 'is-active': activeFilter === 'errors' }"
        @click="activeFilter = 'errors'"
      >
        <span class="filter-card__dot"></span>
        <span class="filter-card__label">Com erro</span>
        <span class="filter-card__count">{{ errorCount }}</span>
      </button>

      <button
        type="button"
        class="filter-card filter-card--valid"
        :class="{ 'is-active': activeFilter === 'valid' }"
        @click="activeFilter = 'valid'"
      >
        <span class="filter-card__dot"></span>
        <span class="filter-card__label">Válidos</span>
        <span class="filter-card__count">{{ validCount }}</span>
      </button>
    </div>

    <div v-if="validCount > limit" class="alert alert-danger mt-2 mb-0">
      Limite máximo de {{ limit.toLocaleString('pt-BR') }} registros válidos excedido
      ({{ validCount.toLocaleString('pt-BR') }}). Remova linhas antes de enviar.
    </div>

    <p v-if="duplicatesRemoved > 0" class="duplicates-info">
      <span v-if="duplicatesRemoved === 1">
        {{ duplicatesRemoved }} linha duplicada foi removida automaticamente
      </span>
      <span v-else>
        {{ duplicatesRemoved }} linhas duplicadas foram removidas automaticamente
      </span>
      <InfoIcon
        size="1x"
        class="duplicates-info__icon"
        v-tooltip.top="'Quando são encontradas linhas com o mesmo CPF no arquivo, as linhas duplicadas são removidas automaticamente durante a importação, mantendo apenas um registro para cada CPF.'"
      />
    </p>

    <table class="table table-borderless table-border table-align table-h51 mt-3">
      <thead>
      <tr>
        <th class="nowrap">Linha<InfoIcon size="1x" class="ml-1 duplicates-info__icon" v-tooltip.right="'Linha original do arquivo'"/></th>
        <th>Nome</th>
        <th>CPF</th>
        <th>Telefone</th>
        <th>Categoria</th>
        <th>Erros</th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="row in pageRows" :key="row._index">
        <td>{{ row._index + 2 }}</td>
        <td><input class="form-control" v-model="row.name"></td>
        <td><input class="form-control" v-mask="'###.###.###-##'" v-model="row.cpf"></td>
        <td><input class="form-control" v-mask="['(##) ####-####', '(##) #####-####']" v-model="row.phone"></td>
        <td><input class="form-control" v-model="row.category"></td>
        <td>
          <span v-if="rowErrors(row).length" class="error-badge">
            <AlertTriangleIcon size="0.9x" class="error-badge__icon" />
            <span class="error-badge__text">{{ rowErrors(row).join(', ') }}</span>
          </span>
          <span v-else class="valid-badge">OK</span>
        </td>
        <td class="text-right">
          <button
            v-if="rowErrors(row).length"
            class="btn-icon btn-outline-red"
            title="Remover linha"
            @click="removeRow(row)"
          >
            <trash-2-icon size="1.5x"/>
          </button>
        </td>
      </tr>
      <tr v-if="!pageRows.length">
        <td colspan="7" class="text-center text-muted py-3">
          Nenhuma linha para exibir com o filtro atual.
        </td>
      </tr>
      </tbody>
    </table>

    <Pagination
      v-model="currentPage"
      :total-count="filteredRows.length"
      :total-pages="totalPages"
      @pagination="currentPage = $event"
    />

    <template slot="footer">
      <div class="footer-content">
        <label class="toggle-switch">
          <input type="checkbox" v-model="updateExisting">
          <span class="toggle-slider"></span>
          <span class="toggle-label">Atualizar motoristas existentes</span>
          <InfoIcon
            size="1x"
            class="toggle-info"
            v-tooltip.top="'Quando um CPF já estiver cadastrado, o registro existente será atualizado com os dados deste arquivo com base no CPF informado. Caso contrário, CPFs já existentes serão reportados como erro.'"
          />
        </label>

        <div class="footer-actions">
          <button class="btn btn-outline mr-2" @click="$emit('input', false)">
            Cancelar
          </button>
          <button
            class="btn btn-primary"
            :disabled="!canSubmit"
            @click="submit"
          >
            Enviar cadastro
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
  import Modal from '@/components/shares/specialTag/Modal'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import GlobalLoading from '@/mixins/GlobalLoading'
  import { isValidCPF } from '@/models/cpf'
  import { Trash2Icon, InfoIcon, AlertTriangleIcon } from 'vue-feather-icons'
  import { mask } from 'vue-the-mask'

  const PAGE_SIZE = 10
  const LIMIT = 10000

  export default {
    components: { Modal, Pagination, Trash2Icon, InfoIcon, AlertTriangleIcon },
    directives: { mask },
    mixins: [GlobalLoading],
    name: 'BatchDriversModal',
    props: {
      value: { default: false, type: Boolean },
      rows: { default: () => [], type: Array },
      duplicatesRemoved: { default: 0, type: Number },
    },
    data() {
      return {
        localRows: [],
        activeFilter: 'all',
        updateExisting: false,
        currentPage: 1,
        limit: LIMIT,
      }
    },
    computed: {
      filteredRows() {
        if (this.activeFilter === 'errors')
          return this.localRows.filter(row => this.rowErrors(row).length > 0)
        if (this.activeFilter === 'valid')
          return this.localRows.filter(row => this.rowErrors(row).length === 0)
        return this.localRows
      },
      totalPages() {
        return Math.ceil(this.filteredRows.length / PAGE_SIZE) || 1
      },
      pageRows() {
        const start = (this.currentPage - 1) * PAGE_SIZE
        return this.filteredRows.slice(start, start + PAGE_SIZE)
      },
      validCount() {
        return this.localRows.filter(row => this.rowErrors(row).length === 0).length
      },
      errorCount() {
        return this.localRows.length - this.validCount
      },
      canSubmit() {
        return this.localRows.length > 0 &&
          this.errorCount === 0 &&
          this.validCount <= this.limit
      },
    },
    methods: {
      rowErrors(row) {
        const errors = []
        if (!row.name) errors.push('Nome é obrigatório')
        if (!row.cpf) errors.push('CPF é obrigatório')
        else if (!isValidCPF(row.cpf)) errors.push('CPF inválido')
        if (!row.phone) errors.push('Telefone é obrigatório')
        else if (![10, 11].includes(String(row.phone).replace(/\D/g, '').length))
          errors.push('Telefone inválido')
        if (!row.category) errors.push('Categoria é obrigatória')
        return errors
      },
      removeRow(row) {
        const index = this.localRows.indexOf(row)
        if (index !== -1) this.localRows.splice(index, 1)
        const lastPage = Math.ceil(this.filteredRows.length / PAGE_SIZE) || 1
        if (this.currentPage > lastPage) this.currentPage = lastPage
      },
      submit() {
        if (!this.canSubmit) {
          if (this.validCount > this.limit)
            this.$notify.addError(`Limite máximo de ${this.limit.toLocaleString('pt-BR')} registros válidos excedido`)
          return
        }

        const header = ['name', 'cpf', 'category', 'phone']
        const onlyDigits = value => String(value).replace(/\D/g, '')
        const data = [
          header,
          ...this.localRows.map(r => [r.name, onlyDigits(r.cpf), r.category, onlyDigits(r.phone)]),
        ]
        const mode = this.updateExisting ? 'create_or_update' : 'create'

        this.openGlobalLoad()
        this.axios.post('drivers/batch_create', { data, mode })
          .then(response => {
            const message = (response.meta && response.meta.message) ||
              'Cadastro de motoristas em lote agendado com sucesso!'
            this.$notify.add(message)
            this.$emit('scheduled', response.data && response.data.id)
            this.$emit('input', false)
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
    },
    watch: {
      rows: {
        immediate: true,
        handler(value) {
          this.localRows = value.map((row, index) => ({ ...row, _index: index }))
          this.currentPage = 1
        },
      },
      activeFilter() {
        this.currentPage = 1
      },
    },
  }
</script>

<style lang="scss">
  .vue-tooltip {
    z-index: 2000 !important;
  }
</style>

<style scoped lang="scss">
  .filter-cards {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
  }

  .filter-card {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #E1E3EB;
    border-radius: 8px;
    border-bottom: 3px solid #E1E3EB;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;

    &:hover {
      background: #F5F7FA;
    }

    &.is-active {
      background: #F5F7FA;
    }

    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #9aa5b1;
      flex-shrink: 0;
    }

    &__label {
      flex: 1;
      font-size: 0.875rem;
      font-weight: 600;
      color: #051B30;
    }

    &__count {
      font-size: 1.25rem;
      font-weight: 700;
      color: #051B30;
    }

    &--all .filter-card__dot { background: #6c757d; }
    &--all.is-active { border-bottom-color: #6c757d; }

    &--errors .filter-card__dot { background: #E93F66; }
    &--errors.is-active { border-bottom-color: #E93F66; }

    &--valid .filter-card__dot { background: #28a745; }
    &--valid.is-active { border-bottom-color: #28a745; }
  }

  .duplicates-info {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: #465059;
    margin: 0 0 4px 4px;

    &__icon {
      color: #2D98DA;
      flex-shrink: 0;
      cursor: help;
    }
  }

  .error-badge {
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #fde8ed;
    color: #E93F66;
    line-height: 1.3;

    &__icon {
      flex-shrink: 0;
      margin-top: 1px;
    }

    &__text {
      flex: 1;
    }
  }

  .valid-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #e4f5e8;
    color: #28a745;
  }

  .footer-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .footer-actions {
    display: flex;
    align-items: center;
  }

  .toggle-switch {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    margin: 0;

    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  .toggle-slider {
    position: relative;
    width: 40px;
    height: 22px;
    background: #cfd8e3;
    border-radius: 22px;
    transition: background 0.2s ease;
    flex-shrink: 0;

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  .toggle-switch input[type="checkbox"]:checked + .toggle-slider {
    background: #2D98DA;

    &::before {
      transform: translateX(18px);
    }
  }

  .toggle-label {
    font-size: 0.875rem;
    color: #051B30;
    font-weight: 500;
  }

  .toggle-info {
    color: #2D98DA;
    cursor: help;
  }
</style>
